import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { useNavigate } from 'react-router-dom';

import { Banner } from 'design-system/src/components/Banner/Banner';
import Button from 'design-system/src/components/Button/Button';
import ChevronLeft from 'design-system/src/assets/icons/chevronLeft.svg?react';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Typography } from 'design-system/src/components/Typography/Typography';

import { formatSimpleDate } from 'shared-utils/utils/formatDate';

import faxVerificationExample from '../assets/faxVerificationExample.png';
import { client } from '../api';
import { TANDEM_CMM_FAX_NUMBER } from '../constants';
import PageContainer from '../components/PageContainer';
import FaxNumberStep from '../components/faxVerification/FaxNumberStep';
import { AgencyDesignationModal } from '../components/faxVerification/AgencyDesignationModal';
import { VerificationCodeInput } from '../components/faxVerification/VerificationCodeInput';
import { VerificationStatus } from '../components/faxVerification/types';

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const FaxVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [submitError, setSubmitError] = useState(false);
  const [lastResendTime, setLastResendTime] = useState<Date | null>(null);

  const RESEND_DELAY_MS = 15 * 60 * 1000; // 15 mins

  // TODO: rename prescriberData to faxVerificationData
  const { data: prescriberData, isLoading: prescriberDataLoading } = useQuery<{
    verification_status: VerificationStatus;
    fax_numbers: string[];
    triggered_fax_number: string | null;
    triggered_at: string | null;
    fax_code_submitted_without_verification_attempt: boolean;
  }>({
    queryKey: ['prescriber-verifications'],
    queryFn: async () => {
      // TODO: rename endpoint
      const response = await client.get('/verification/prescribers');
      return response.data;
    },
  });

  const reTriggerFax = useMutation<void, Error, { fax_number: string }>({
    mutationFn: async ({ fax_number }) => {
      await client.post('/verification/retrigger-message', { fax_number });
    },
    onSuccess: () => {
      setLastResendTime(new Date());
      toast.custom(() => <Toast variant="success">Verification fax sent.</Toast>);
    },
    onError: (error) => {
      console.error('Error retriggering fax:', error);
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const submitCode = useMutation<void, Error, { code: string }>({
    mutationFn: async ({ code }) => {
      await client.post('/verification/submit-code', {
        verification_code: code,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriber-verifications'] });
      navigate('/prescriptions');
    },
    onError: (error) => {
      console.error('Error submitting verification code:', error);
      Sentry.captureException(error);
      setSubmitError(true);
    },
  });

  const resetToPending = useMutation<void, Error>({
    mutationFn: async () => {
      await client.post('/verification/reset-to-pending');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriber-verifications'] });
      setVerificationCode('');
      setSubmitError(false);
    },
    onError: (error) => {
      console.error('Error resetting verification:', error);
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  if (prescriberDataLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (!prescriberData) {
    return <PageLoadError />;
  }

  const step1Statuses: VerificationStatus[] = ['pending', 'no_fax_numbers_available', 'no_valid_fax_numbers'];
  const currentStep = step1Statuses.includes(prescriberData.verification_status) ? 1 : 2;
  const stepTitle = currentStep === 1 ? 'Trigger the fax' : 'Enter the 8-digit code';

  // Resend is enabled if 15 minutes have passed since the last trigger
  const lastTriggerTime =
    lastResendTime ?? (prescriberData.triggered_at ? new Date(prescriberData.triggered_at) : null);
  const resendEnabled = lastTriggerTime ? Date.now() - lastTriggerTime.getTime() >= RESEND_DELAY_MS : true;

  return (
    <PageContainer noPadding>
      <Button variant="tertiary" to="/onboarding" size="sm" className="gap-1 px-3 py-2" style={{ border: 'none' }}>
        <ChevronLeft aria-hidden={true} className="size-4 text-grayscale-900" />
        <Typography renderedAs="span" styledAs="bodySpaceGrotesk" weight={500}>
          Back
        </Typography>
      </Button>
      <div className="mx-auto max-w-main-content p-5">
        <div className="flex flex-col gap-4">
          <Typography styledAs="h6" renderedAs="h1">
            Verify your fax number
          </Typography>
          <Typography styledAs="bodySmallSpaceGrotesk" color="var(--grayscale-600)">
            To process your prior authorizations with Tandem, you need to verify a fax number for your practice.
          </Typography>
        </div>
        <div className="mt-8 flex flex-col gap-6">
          {currentStep === 2 && prescriberData.triggered_fax_number && (
            <Banner
              variant="success"
              title={`Fax has been sent to ${prescriberData.triggered_fax_number}`}
              description={
                <span className="text-grayscale-600">
                  The fax from <span className="font-bold">{TANDEM_CMM_FAX_NUMBER}</span>
                  {prescriberData.triggered_at && (
                    <>
                      {' '}
                      was triggered on {formatSimpleDate(prescriberData.triggered_at)} at{' '}
                      {new Date(prescriberData.triggered_at)
                        .toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                        .toLowerCase()
                        .replace(' ', '')}{' '}
                      and
                    </>
                  )}{' '}
                  typically arrives within 15 minutes.
                </span>
              }
              className="!justify-start rounded-medium"
            />
          )}
          <div className="flex flex-col gap-6 rounded-lg border border-solid border-[#F7F6F7] bg-white p-4 shadow-container">
            <div className="flex flex-col gap-2">
              <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                Step {currentStep} of 2
              </Typography>
              <Typography renderedAs="h2" styledAs="bodyLargeSpaceGrotesk" weight={600}>
                {stepTitle}
              </Typography>
              {currentStep === 1 && (
                <>
                  <Typography styledAs="bodyDMSans" weight={400} color="var(grayscale-600)">
                    Choose a fax number below to receive a verification fax from CoverMyMeds, which we set up for your
                    practice to process prior authorizations.
                  </Typography>
                  <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-500)">
                    By clicking <span className="font-semibold">Trigger fax</span>, you agree to{' '}
                    <Button
                      variant="link"
                      onClick={() => setIsTermsModalOpen(true)}
                      textStyledAs="bodyExtraSmallDMSans"
                      textWeight={400}
                      style={{ color: 'var(--grayscale-500)' }}
                    >
                      this agency designation
                    </Button>{' '}
                    with regard to Tandem creating an account for you.
                  </Typography>
                </>
              )}
            </div>
            {currentStep === 1 && <FaxNumberStep prescriberData={prescriberData} />}
            {currentStep === 2 && (
              <>
                <div className="flex flex-col items-center gap-6 overflow-hidden rounded-medium border border-solid border-grayscale-200 pb-6">
                  <img
                    src={faxVerificationExample}
                    alt="Example fax from CoverMyMeds showing where to find the 8-digit verification code"
                    className="w-full"
                  />
                  <div className="flex w-full flex-col gap-4 px-6">
                    <Typography styledAs="bodyDMSans" weight={400} color="var(--grayscale-600)" textAlign="center">
                      Locate the 8-digit code below "Your verification code for NPI(s)" in the fax document above.
                    </Typography>
                    <VerificationCodeInput
                      value={verificationCode}
                      onChange={(value) => {
                        setVerificationCode(value);
                        setSubmitError(false);
                      }}
                    />
                    <ErrorMessage visible={submitError} textStyledAs="bodySmallSpaceGrotesk">
                      The code you entered is incorrect. Please try again.
                    </ErrorMessage>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => resetToPending.mutate()}
                    isLoading={resetToPending.isPending}
                  >
                    Select alternate fax number
                  </Button>
                  <div className="flex items-center gap-2">
                    {resendEnabled ? (
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => {
                          if (prescriberData.triggered_fax_number) {
                            reTriggerFax.mutate({ fax_number: prescriberData.triggered_fax_number });
                          }
                        }}
                        isLoading={reTriggerFax.isPending}
                      >
                        Resend fax
                      </Button>
                    ) : (
                      <Tooltip content="Unavailable within 15 min of a prior fax." childNotInteractive>
                        <Button variant="tertiary" size="sm" disabled>
                          Resend fax
                        </Button>
                      </Tooltip>
                    )}
                    <Button
                      size="sm"
                      disabled={verificationCode.length !== 8}
                      isLoading={submitCode.isPending}
                      onClick={() => submitCode.mutate({ code: verificationCode })}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
          {isTermsModalOpen && <AgencyDesignationModal onClose={() => setIsTermsModalOpen(false)} />}
        </div>
      </div>
    </PageContainer>
  );
};

export default FaxVerificationPage;
